import { useEffect, useCallback, useRef } from 'react';

export const usePerformanceOptimization = () => {
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Lazy load images
  const lazyLoadImages = useCallback(() => {
    const images = document.querySelectorAll('img[data-src]');
    
    if (images.length === 0) return;

    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target as HTMLImageElement;
            const src = img.getAttribute('data-src');
            if (src) {
              img.src = src;
              img.removeAttribute('data-src');
              img.classList.remove('lazy');
              observerRef.current?.unobserve(img);
            }
          }
        });
      }, {
        rootMargin: '50px 0px',
        threshold: 0.01
      });
    }

    images.forEach((img) => {
      observerRef.current?.observe(img);
    });
  }, []);

  // Preload critical resources
  const preloadCriticalResources = useCallback(() => {
    const criticalResources = [
      '/static/css/main.css',
      '/static/js/main.js',
      '/logo192.png',
      '/logo512.png'
    ];

    criticalResources.forEach((resource) => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = resource;
      link.as = resource.endsWith('.css') ? 'style' : 
                 resource.endsWith('.js') ? 'script' : 'image';
      document.head.appendChild(link);
    });
  }, []);

  // Optimize fonts loading
  const optimizeFonts = useCallback(() => {
    // Add font-display: swap for better performance
    const fontLinks = document.querySelectorAll('link[rel="preload"][as="font"]');
    fontLinks.forEach((link) => {
      const fontLink = link as HTMLLinkElement;
      fontLink.setAttribute('font-display', 'swap');
    });
  }, []);

  // Add resource hints
  const addResourceHints = useCallback(() => {
    const hints = [
      { rel: 'dns-prefetch', href: '//fonts.googleapis.com' },
      { rel: 'dns-prefetch', href: '//fonts.gstatic.com' },
      { rel: 'preconnect', href: '//fonts.googleapis.com' },
      { rel: 'preconnect', href: '//fonts.gstatic.com' },
      { rel: 'preconnect', href: '//www.google-analytics.com' },
      { rel: 'preconnect', href: '//www.googletagmanager.com' }
    ];

    hints.forEach((hint) => {
      const link = document.createElement('link');
      link.rel = hint.rel;
      link.href = hint.href;
      document.head.appendChild(link);
    });
  }, []);

  // Optimize CSS delivery
  const optimizeCSSDelivery = useCallback(() => {
    const criticalCSS = `
      /* Critical CSS for above-the-fold content */
      .hero-section { opacity: 1; }
      .loading-spinner { display: none; }
      .content-protected { user-select: none; }
    `;

    const style = document.createElement('style');
    style.textContent = criticalCSS;
    style.setAttribute('data-critical', 'true');
    document.head.insertBefore(style, document.head.firstChild);
  }, []);

  // Defer non-critical JavaScript
  const deferNonCriticalJS = useCallback(() => {
    const scripts = document.querySelectorAll('script[data-defer]');
    scripts.forEach((script) => {
      const scriptElement = script as HTMLScriptElement;
      scriptElement.defer = true;
    });
  }, []);

  // Optimize images
  const optimizeImages = useCallback(() => {
    const images = document.querySelectorAll('img');
    images.forEach((img) => {
      const imageElement = img as HTMLImageElement;
      
      // Add loading="lazy" for images below the fold
      if (!imageElement.hasAttribute('loading')) {
        imageElement.loading = 'lazy';
      }
      
      // Add decoding="async" for better performance
      if (!imageElement.hasAttribute('decoding')) {
        imageElement.decoding = 'async';
      }
      
      // Add sizes attribute for responsive images
      if (imageElement.classList.contains('responsive') && !imageElement.hasAttribute('sizes')) {
        imageElement.sizes = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw';
      }
    });
  }, []);

  // Add service worker for caching
  const addServiceWorker = useCallback(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('SW registered: ', registration);
        })
        .catch((registrationError) => {
          console.log('SW registration failed: ', registrationError);
        });
    }
  }, []);

  // Optimize animations
  const optimizeAnimations = useCallback(() => {
    // Add will-change property for elements with animations
    const animatedElements = document.querySelectorAll('.animate-fade-in-up, .animate-float, .hover\\:scale-105');
    animatedElements.forEach((element) => {
      const el = element as HTMLElement;
      el.style.willChange = 'transform, opacity';
    });
  }, []);

  useEffect(() => {
    // Initialize performance optimizations
    preloadCriticalResources();
    addResourceHints();
    optimizeCSSDelivery();
    deferNonCriticalJS();
    optimizeFonts();
    optimizeImages();
    optimizeAnimations();
    
    // Add service worker
    addServiceWorker();

    // Lazy load images after DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', lazyLoadImages);
    } else {
      lazyLoadImages();
    }

    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [
    preloadCriticalResources,
    addResourceHints,
    optimizeCSSDelivery,
    deferNonCriticalJS,
    optimizeFonts,
    optimizeImages,
    optimizeAnimations,
    addServiceWorker,
    lazyLoadImages
  ]);

  return {
    lazyLoadImages,
    optimizeImages,
    optimizeAnimations
  };
};
